import { Link } from 'react-router-dom';
import { AlertTriangle, Loader2, Lock, Mail, RefreshCw } from 'lucide-react';

import {
  useStripeMethods,
  useAutopayEnrollments,
  useSetAutopay,
  type AutopayScope,
} from '@/hooks/useStripeMethods';
import { stripeMethodLabel } from '@/lib/stripe';
import { PAY_METHOD_LABEL, type PayMethod } from '@/lib/payMethod';
import { REMIT_TO_EMAIL } from '@/lib/payToUs';

/**
 * StripeAutopayPanel — how each section of the customer's business gets paid.
 * Policy (docs/STRIPE-AUTOPAY.md): Equipment + Cylinder rentals REQUIRE an
 * arrangement; Product orders are optional. Each may use a different method.
 *
 * ⚠ ONE dropdown per section, deliberately. It offers the saved cards/banks
 * AND "I'll mail checks" / "I'll send ACH", because from the customer's side
 * that is a single question — and separating them let a section claim autopay
 * and self-pay at once, which is how an invoice gets paid twice. The endpoint
 * enforces the same exclusivity server-side.
 *
 * This is the "give them the ability" screen. Automatic charging itself is
 * gated (STRIPE_AUTOPAY_ENABLED) and switched on separately.
 */

const SCOPES: Array<{ scope: AutopayScope; label: string; blurb: string; required: boolean }> = [
  { scope: 'equipment', label: 'Equipment rentals', blurb: 'Monthly equipment / fountain rental invoices.', required: true },
  { scope: 'cylinder', label: 'Cylinder rentals', blurb: 'Monthly gas cylinder rental fee invoices.', required: true },
  { scope: 'product', label: 'Product orders', blurb: 'Optional — auto-pay your product invoices too.', required: false },
];

export function StripeAutopayPanel() {
  const methodsQuery = useStripeMethods(true);
  const methods = methodsQuery.data?.methods ?? [];
  const enrollQuery = useAutopayEnrollments(true);
  const setAutopay = useSetAutopay();

  const enrollmentFor = (scope: AutopayScope) =>
    (enrollQuery.data?.enrollments ?? []).find((e) => e.scope === scope) ?? null;
  const payMethods = enrollQuery.data?.pay_methods ?? {};
  // Set when a chain master pays these invoices — the arrangement is theirs.
  const managedBy = enrollQuery.data?.managed_by ?? null;

  if (methodsQuery.isLoading || enrollQuery.isLoading) {
    return (
      <p className="text-sm text-brix-muted flex items-center gap-2">
        <Loader2 size={14} className="animate-spin" /> Loading autopay…
      </p>
    );
  }

  const noMethods = methods.length === 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-brix-muted leading-relaxed rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5">
        Automatic charging on the new system is being switched on. Your choices here are saved and
        will be used once it's live.
      </p>

      {noMethods && (
        <div className="rounded-xl border border-brix-border bg-brix-surface-alt/50 px-4 py-3 text-base leading-relaxed text-slate-700">
          You have no saved card or bank. You can add one in{' '}
          <Link to="/settings/payments" className="font-semibold text-brix-primary hover:underline">
            Settings → Payment methods
          </Link>{' '}
          and let us charge it — or tell us below that you'll mail checks or send us ACH, which
          needs nothing saved.
        </div>
      )}

      {setAutopay.isError && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {setAutopay.error.message}
        </p>
      )}

      {SCOPES.map(({ scope, label, blurb, required }) => {
        const enr = enrollmentFor(scope);
        const selfPay = (payMethods[scope] ?? null) as PayMethod | null;
        // A section they've said they'll pay themselves is ARRANGED, so it
        // must not keep nagging — "required" means an arrangement exists, not
        // that we hold a card.
        const arranged = !!enr || (selfPay !== null && selfPay !== 'card' && selfPay !== 'ach');
        const missingRequired = required && !arranged;
        return (
          <div key={scope} className="rounded-2xl border border-brix-border bg-brix-surface p-4">
            <div className="flex items-center gap-2">
              <RefreshCw size={17} className="text-brix-primary" />
              <span className="text-base font-bold text-slate-900">{label}</span>
              {required ? (
                <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  Required
                </span>
              ) : (
                <span className="rounded-md border border-brix-border bg-brix-surface-alt px-2 py-0.5 text-xs font-semibold text-brix-muted">
                  Optional
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-brix-muted">{blurb}</p>

            <div className="mt-3 flex items-center gap-2">
              <select
                value={enr ? enr.stripe_pm_id : selfPay ?? ''}
                disabled={!!managedBy || setAutopay.isPending}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') setAutopay.mutate({ scope, off: true });
                  else if (v.startsWith('pm_')) setAutopay.mutate({ scope, stripe_pm_id: v });
                  else setAutopay.mutate({ scope, self_pay: v as PayMethod });
                }}
                className="flex-1 rounded-lg border border-brix-border bg-brix-surface px-3 py-2.5 text-base text-slate-900 disabled:opacity-50"
              >
                <option value="">{required ? 'Choose how this is paid…' : 'Not set'}</option>
                {methods.length > 0 && (
                  <optgroup label="We charge you automatically">
                    {methods.map((m) => (
                      <option key={m.stripe_pm_id} value={m.stripe_pm_id}>
                        {stripeMethodLabel(m)}
                      </option>
                    ))}
                  </optgroup>
                )}
                {/* Always offered — these need nothing saved, which is the
                    whole point for a customer who won't connect an account. */}
                <optgroup label="I'll pay it myself">
                  <option value="check">{PAY_METHOD_LABEL.check}</option>
                  <option value="ach_push">{PAY_METHOD_LABEL.ach_push}</option>
                </optgroup>
              </select>
            </div>

            {managedBy && (
              <p className="mt-2 flex items-start gap-1.5 text-sm text-brix-muted">
                <Lock size={14} className="mt-0.5 shrink-0" />
                {managedBy.name} pays these invoices, so this is set on their account.
              </p>
            )}

            {selfPay === 'ach_push' && !managedBy && (
              <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-relaxed text-amber-900">
                <Mail size={14} className="mt-0.5 shrink-0" />
                <span>
                  Please email your remittance to{' '}
                  <a href={`mailto:${REMIT_TO_EMAIL}`} className="font-bold underline">
                    {REMIT_TO_EMAIL}
                  </a>{' '}
                  so we can apply it to the right invoices. Our bank details are on every
                  invoice and on the{' '}
                  <Link to="/pay?selfpay=1" className="font-bold underline">
                    Pay page
                  </Link>{' '}
                  — nothing to set up on your side.
                </span>
              </p>
            )}

            {selfPay === 'check' && !managedBy && (
              <p className="mt-2 text-sm text-brix-muted">
                Write your invoice numbers on the check — the mailing address is on the{' '}
                <Link to="/pay" className="font-semibold text-brix-primary hover:underline">
                  Pay
                </Link>{' '}
                page and every invoice.
              </p>
            )}

            {missingRequired && !managedBy && (
              <p className="mt-2 flex items-start gap-1.5 text-sm font-semibold text-amber-800">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                We need to know how this gets paid — choose an option above.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
